import React from "react";
import { Link } from "react-router-dom";
import { Lightbulb, Zap, Thermometer, Shield, Lock, ArrowUpRight } from "lucide-react";

const TagBadge = ({ tag }) => {
  if (!tag) return null;
  const isNew = tag === "NEW";
  return (
    <span
      data-testid={`product-tag-${tag.toLowerCase()}`}
      className={`inline-flex items-center text-[10px] font-bold tracking-[0.16em] uppercase px-2.5 py-1 ${
        isNew ? "bg-leduro-orange text-white" : "bg-leduro-navy text-white"
      }`}
    >
      {tag}
    </span>
  );
};

const WarrantyBadge = ({ years }) => (
  <span className="inline-flex items-center text-[10px] font-bold tracking-[0.16em] uppercase px-2.5 py-1 bg-amber-200/80 text-leduro-navy">
    {years}Y warranty
  </span>
);

const Spec = ({ Icon, value }) => (
  <span className="inline-flex items-center gap-1.5 border border-leduro-border px-2.5 py-1.5 text-xs font-mono text-leduro-navy bg-white">
    <Icon className="h-3.5 w-3.5 text-leduro-orange" />
    {value}
  </span>
);

export const ProductCard = ({ product, viewMode = "grid", compared, onCompare }) => {
  const isList = viewMode === "list";
  const productPath = `/product/${product.id}`;

  if (isList) {
    return (
      <div
        data-testid={`product-card-${product.id}`}
        className="leduro-card group flex flex-col sm:flex-row overflow-hidden"
      >
        <Link
          to={productPath}
          data-testid={`product-link-${product.id}`}
          className="relative sm:w-64 shrink-0 aspect-[4/3] sm:aspect-auto bg-leduro-surface border-b sm:border-b-0 sm:border-r border-leduro-border overflow-hidden"
        >
          <img
            src={product.image}
            alt={product.name}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute top-2 left-2 flex flex-col items-start gap-1">
            <TagBadge tag={product.tag} />
            <WarrantyBadge years={product.warrantyYears} />
          </div>
        </Link>
        <div className="flex-1 p-5 flex flex-col">
          <div className="flex items-start justify-between gap-3">
            <div>
              <span className="text-[11px] font-mono text-leduro-muted tracking-wider">
                {product.sku} · {product.category.replace(/-/g, " ").replace(/led /i, "")}
              </span>
              <Link to={productPath} data-testid={`product-title-${product.id}`}>
                <h3 className="mt-1 font-heading font-bold text-leduro-navy text-lg group-hover:text-leduro-orange transition-colors">
                  {product.name}
                </h3>
              </Link>
            </div>
            <label
              className="flex items-center gap-2 text-xs text-leduro-muted cursor-pointer shrink-0"
              onClick={(e) => e.stopPropagation()}
            >
              <input
                type="checkbox"
                checked={!!compared}
                onChange={() => onCompare?.(product.id)}
                data-testid={`compare-${product.id}`}
                className="accent-leduro-orange"
              />
              Compare
            </label>
          </div>
          <div className="mt-3 flex flex-wrap gap-1.5">
            <Spec Icon={Lightbulb} value={product.lumen} />
            <Spec Icon={Zap} value={product.watt} />
            <Spec Icon={Thermometer} value={product.cct} />
            <Spec Icon={Shield} value={product.ip} />
          </div>
          <div className="mt-auto pt-4 flex items-center justify-between gap-3 border-t border-dashed border-leduro-border">
            <span className="inline-flex items-center gap-1.5 text-sm text-leduro-muted">
              <Lock className="h-3.5 w-3.5" />
              Price{" "}
              <Link
                to="/login"
                className="text-leduro-orange underline underline-offset-2 font-semibold"
                data-testid={`signin-${product.id}`}
              >
                sign in
              </Link>
            </span>
            <Link
              to={productPath}
              data-testid={`product-view-${product.id}`}
              className="inline-flex items-center gap-1.5 text-[11px] uppercase font-bold tracking-wider text-leduro-navy hover:text-leduro-orange"
            >
              View product <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </div>
          <span className="text-[10px] font-mono tracking-wider text-leduro-muted uppercase mt-1">
            MIN {product.minQty} PCS
          </span>
        </div>
      </div>
    );
  }

  // Grid view
  return (
    <div
      data-testid={`product-card-${product.id}`}
      className="leduro-card group relative flex flex-col"
    >
      <Link
        to={productPath}
        data-testid={`product-link-${product.id}`}
        className="relative aspect-[4/3] bg-leduro-surface overflow-hidden border-b border-leduro-border block"
        aria-label={`Open ${product.name}`}
      >
        <img
          src={product.image}
          alt={product.name}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute top-3 left-3 flex flex-col items-start gap-1.5 pointer-events-none">
          <TagBadge tag={product.tag} />
          <WarrantyBadge years={product.warrantyYears} />
        </div>
        <span className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 bg-white/95 border border-leduro-border px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-wider text-leduro-navy translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all">
          Open <ArrowUpRight className="h-3.5 w-3.5 text-leduro-orange" />
        </span>
      </Link>

      {/* Compare checkbox — outside the Link, top-right */}
      <label
        className="absolute top-3 right-3 z-10 inline-flex items-center gap-1.5 bg-white/95 border border-leduro-border px-2 py-1 text-[11px] text-leduro-navy cursor-pointer hover:border-leduro-orange transition-colors"
        onClick={(e) => e.stopPropagation()}
      >
        <input
          type="checkbox"
          checked={!!compared}
          onChange={() => onCompare?.(product.id)}
          data-testid={`compare-${product.id}`}
          className="accent-leduro-orange h-3 w-3"
        />
        Compare
      </label>

      <div className="p-5 flex flex-col flex-1">
        <span className="text-[11px] font-mono text-leduro-muted tracking-wider">
          {product.sku} · {product.category.replace(/-/g, " ").replace(/led /i, "")}
        </span>
        <Link to={productPath} data-testid={`product-title-${product.id}`}>
          <h3 className="mt-1 font-heading font-bold text-leduro-navy text-lg leading-snug group-hover:text-leduro-orange transition-colors">
            {product.name}
          </h3>
        </Link>

        <div className="mt-4 flex flex-wrap gap-1.5">
          <Spec Icon={Lightbulb} value={product.lumen} />
          <Spec Icon={Zap} value={product.watt} />
          <Spec Icon={Thermometer} value={product.cct} />
          <Spec Icon={Shield} value={product.ip} />
        </div>

        <div className="mt-auto pt-4 flex items-center justify-between gap-3 border-t border-dashed border-leduro-border">
          <span className="inline-flex items-center gap-1.5 text-sm text-leduro-muted">
            <Lock className="h-3.5 w-3.5" />
            Price{" "}
            <Link
              to="/login"
              className="text-leduro-orange underline underline-offset-2 font-semibold"
              data-testid={`signin-${product.id}`}
            >
              sign in
            </Link>
          </span>
          <Link
            to={productPath}
            data-testid={`product-view-${product.id}`}
            className="inline-flex items-center gap-1.5 text-[11px] uppercase font-bold tracking-wider text-leduro-navy hover:text-leduro-orange"
          >
            View <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        </div>
        <span className="text-[10px] font-mono tracking-wider text-leduro-muted uppercase mt-1">
          MIN {product.minQty} PCS
        </span>
      </div>
    </div>
  );
};

export default ProductCard;
